import React, {useEffect, useState} from "react";
import Grid from "@mui/material/Unstable_Grid2";
import {Box, Typography, useMediaQuery, useTheme} from "@mui/material";
import {Trans, useTranslation} from "react-i18next";
import {TotalCostProps} from "./interfaces";
import {getFormattedPrice} from "../../../utils/formatCurrency.utils";
import {
  AccountingItemDto,
  AccountingItemsGetAdditionalServicesApiArg,
  useAccountingItemsGetAdditionalServicesQuery,
} from "../../features/accountingItem/accountingItems-api";
import {toastError} from "../../common/utils/toastMessages";
import i18next from "i18next";
import {ChargeDto} from "../../features/order/order-api";
import {LoadingButton} from "@mui/lab";
import {Field, Formik} from "formik";
import {TextField} from "formik-mui";
import {useAppSelector} from "../../store";
import {
  useWorkflowExecutionMutation,
  WorkflowExecutionApiArg,
} from "../../features/workflowTriggers/workflowExecution-api";
import toast from "react-hot-toast";

export const boxStyle = {
  backgroundColor: "white",
  border: "1px solid #D8E6FB",
  borderRadius: "16px",
  boxShadow: "0px 0px 8px rgba(15, 74, 132, 0.1)",
  p: 4,
};

const TotalCost = ({
  deliveryCostPerUnit,
  sx = null,
  charges,
  consolidationItemCode = "",
  insuranceItemCode = "",
  showTotalCost = false,
  applyPromoCode,
  currentOrder,
}: TotalCostProps) => {
  const {t} = useTranslation();

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [accountingItems, setAccountingItems] = useState<
    AccountingItemDto[] | undefined
  >([]);
  const [shippingCharges, setShippingCharges] =
    useState<ChargeDto[] | null | undefined>(null);
  const [insuranceCharge, setInsuranceCharge] =
    useState<ChargeDto | null | undefined>(null);
  const [discountCharge, setDiscountCharge] =
    useState<ChargeDto | null | undefined>(null);
  const [consolidationCharge, setConsolidationCharge] =
    useState<ChargeDto | null | undefined>(null);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [promoCode, setPromoCode] = useState<string>(
    currentOrder?.customValues?.promoCode,
  );
  const [promoCodeLoad, setPromoCodeLoad] = useState<boolean>(false);

  const [additionalCharges, setAdditionalCharges] = useState<
    ChargeDto[] | undefined
  >([]);

  useEffect(() => {
    if (showTotalCost) {
      let total = 0;

      if (shippingCharges && shippingCharges.length > 0) {
        shippingCharges.forEach((charge) => (total += charge.totalAmount ?? 0));
      }
      if (insuranceCharge && insuranceCharge.totalAmount) {
        total += insuranceCharge.totalAmount;
      }
      if (discountCharge && discountCharge.totalAmount) {
        total += discountCharge.totalAmount;
      }
      if (consolidationCharge && consolidationCharge.totalAmount) {
        total += consolidationCharge.totalAmount;
      }
      if (additionalCharges && additionalCharges.length > 0) {
        additionalCharges.forEach(
          (charge) => (total += charge.totalAmount ?? 0),
        );
      }

      setTotalCost(total);
    }
  }, [
    shippingCharges,
    insuranceCharge,
    discountCharge,
    consolidationCharge,
    additionalCharges,
    showTotalCost,
  ]);

  const args: AccountingItemsGetAdditionalServicesApiArg = {
    organizationId: process.env
      .REACT_APP_PORTAL_ORGANIZATION_ID as unknown as number,
  };

  const validatePromoCodeWorkflowId = useAppSelector(
    (state: any) =>
      state.organizationConfigState?.workflows?.validatePromoCodeWorkflowId,
  );
  const [runWorkflow] = useWorkflowExecutionMutation();

  const {
    data: servicesData,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useAccountingItemsGetAdditionalServicesQuery(args);

  useEffect(() => {
    if (isSuccess) {
      setAccountingItems(servicesData);
    }
    if (isError) {
      toastError(error);
    }
  }, [isLoading]);

  useEffect(() => {
    const shipCharges = charges?.filter(
      (x) => x.accountingItemName?.toLowerCase() == "shipping",
    );
    setShippingCharges(shipCharges);
    const insCharge = charges?.find(
      (x) => x.accountingItemCode == insuranceItemCode,
    );
    setInsuranceCharge(insCharge);
    const disCharge = charges?.find((x) => x.applyBy == "Calculated");
    setDiscountCharge(disCharge);
    const consCharge = charges?.find(
      (x) =>
        consolidationItemCode && x.accountingItemCode == consolidationItemCode,
    );
    setConsolidationCharge(consCharge);
    const addCharges = charges?.filter((x) => {
      return (
        x.accountingItemCode &&
        x.accountingItemCode !== insuranceItemCode &&
        x.accountingItemCode !== consolidationItemCode &&
        !x.accountingItemCode.toLowerCase().includes("discount") &&
        x.applyBy !== "Calculated"
      );
    });
    setAdditionalCharges(addCharges);
  }, [charges, consolidationItemCode, insuranceItemCode]);

  async function confirmPromoCode() {
    setPromoCodeLoad(true);
    const workflowVariables: any = {
      rateId: currentOrder.deliveryMethod?.rateId,
      promoCode: promoCode,
    };
    const executeQuoteWorkflowApiArgs: WorkflowExecutionApiArg = {
      organizationId: process.env
        .REACT_APP_PORTAL_ORGANIZATION_ID as unknown as number,
      workflowId: validatePromoCodeWorkflowId,
      values: {variables: {...workflowVariables}},
    };
    const response: any = await runWorkflow(executeQuoteWorkflowApiArgs);
    if (response.data?.outputs) {
      await applyPromoCode(promoCode);
      toast.success(t("delivery.additionalServices.promoCodeApplied"));
    }
    if (response.error) {
      setPromoCode("");
      toastError(response);
    }
    setPromoCodeLoad(false);
  }

  return (
    <Box sx={sx ?? boxStyle}>
      <Grid container direction="column">
        <Typography variant={isDesktop ? "h2" : "h3"} mb={3}>
          {showTotalCost
            ? t("delivery.totalCost.shippingCost")
            : t("delivery.totalCost.estimatedCost")}
        </Typography>

        <Grid container direction="row" justifyContent="space-between">
          <Typography sx={{fontWeight: 600}} variant="body1">
            {t("delivery.additionalServices.shippingCost")}
          </Typography>
          <Grid borderBottom="1px dotted #D8E6FB" flexGrow={1} height="20px" />
          <Typography
            sx={{fontWeight: 600}}
            data-testid="delivery-cost"
            variant="body4"
          >
            {deliveryCostPerUnit}
          </Typography>
        </Grid>

        {insuranceCharge && !isNaN(insuranceCharge?.totalAmount as any) && (
          <Grid
            sx={{pt: 3}}
            container
            direction="row"
            justifyContent="space-between"
          >
            <Typography sx={{fontWeight: 600}} variant="body1">
              {t("delivery.additionalServices.insurance")}
            </Typography>
            <Grid
              borderBottom="1px dotted #D8E6FB"
              flexGrow={1}
              height="20px"
            />
            <Typography
              sx={{fontWeight: 600}}
              data-testid="insurance-cost"
              variant="body4"
            >
              {getFormattedPrice(insuranceCharge.totalAmount)}
            </Typography>
          </Grid>
        )}

        {discountCharge && !isNaN(discountCharge?.price as any) && (
          <Grid
            sx={{pt: 3}}
            container
            direction="row"
            justifyContent="space-between"
          >
            <Typography sx={{fontWeight: 600}} variant="body1">
              {t("delivery.additionalServices.discount")}
            </Typography>
            <Grid
              borderBottom="1px dotted #D8E6FB"
              flexGrow={1}
              height="20px"
            />
            <Typography
              sx={{fontWeight: 600}}
              data-testid="discount-percent"
              variant="body4"
            >
              {discountCharge.price
                ? `${(Math.abs(discountCharge?.price) * 100).toFixed(2)}%`
                : ""}
            </Typography>
          </Grid>
        )}

        {additionalCharges?.length !== 0 && (
          <>
            <Grid
              sx={{pt: 3}}
              container
              direction="row"
              justifyContent="space-between"
            >
              <Typography sx={{fontWeight: 600}} variant="body1">
                {t("delivery.statusBar.additionalServices")}
              </Typography>
            </Grid>
            {additionalCharges?.map((data: any, index: number) => {
              const accountingItem = accountingItems?.find(
                (x) => x.accountingItemId === data.accountingItemId,
              );
              return (
                <Grid
                  sx={{pt: 1}}
                  key={index}
                  container
                  direction="row"
                  justifyContent="space-between"
                >
                  <Typography variant="body1">
                    {i18next.language == "en" &&
                      accountingItem?.customValues?.service_description_en}
                    {i18next.language == "ru" &&
                      accountingItem?.customValues?.service_description_ru}
                    {i18next.language == "ua" &&
                      accountingItem?.customValues?.service_description_ua}
                  </Typography>
                  <Grid
                    borderBottom="1px dotted #D8E6FB"
                    flexGrow={1}
                    height="20px"
                  />
                  <Typography
                    sx={{fontWeight: 600}}
                    data-testid="approved-additional-services-cost"
                    variant="body4"
                  >
                    {getFormattedPrice(data.totalAmount)}
                  </Typography>
                </Grid>
              );
            })}
          </>
        )}
        {consolidationCharge &&
          !isNaN(consolidationCharge?.totalAmount as any) && (
            <Grid
              sx={{pt: 3}}
              container
              direction="row"
              justifyContent="space-between"
            >
              <Typography sx={{fontWeight: 600}} variant="body1">
                {i18next.language == "en" &&
                  consolidationCharge.accountingItem?.customValues
                    ?.service_description_en}
                {i18next.language == "ru" &&
                  consolidationCharge.accountingItem?.customValues
                    ?.service_description_ru}
                {i18next.language == "ua" &&
                  consolidationCharge.accountingItem?.customValues
                    ?.service_description_ua}
              </Typography>
              <Grid
                borderBottom="1px dotted #D8E6FB"
                flexGrow={1}
                height="20px"
              />
              <Typography
                sx={{fontWeight: 600}}
                data-testid="consolidation-cost"
                variant="body4"
              >
                {consolidationCharge.totalAmount
                  ? getFormattedPrice(consolidationCharge.totalAmount)
                  : ""}
              </Typography>
            </Grid>
          )}
        {showTotalCost && (
          <Grid
            sx={{pt: 3}}
            container
            direction="row"
            justifyContent="space-between"
          >
            <Typography sx={{fontWeight: 600}} variant="body1">
              <Trans i18nKey={"delivery.totalCost.total"}>Total Cost</Trans>
            </Typography>
            <Grid
              borderBottom="1px dotted #D8E6FB"
              flexGrow={1}
              height="20px"
            />
            <Typography
              sx={{fontWeight: 600}}
              data-testid="total-cost"
              variant="body4"
            >
              {getFormattedPrice(totalCost)}
            </Typography>
          </Grid>
        )}
        {applyPromoCode && (
          <div
            style={{
              display: isDesktop ? "flex" : "block",
              alignItems: "center",
              marginTop: "20px",
            }}
          >
            <Formik
              initialValues={{promoCode}}
              onSubmit={() => applyPromoCode()}
            >
              <Field
                fullWidth
                component={TextField}
                name={`promoCode`}
                size={isDesktop ? "normal" : "small"}
                label={t("delivery.additionalServices.promoCode")}
                placeholder={t("delivery.additionalServices.promoCode")}
                data-testid="input-promocode"
                style={{
                  marginRight: "20px",
                  maxWidth: "200px",
                }}
                value={promoCode}
                onChange={(e: any) => {
                  setPromoCode(e.target.value);
                }}
              />
            </Formik>
            <LoadingButton
              size={"small"}
              variant={"outlined"}
              color={"primary"}
              sx={{height: "40px", textTransform: "none", maxWidth: "100px"}}
              loading={promoCodeLoad}
              onClick={confirmPromoCode}
              disabled={!promoCode || promoCode == ""}
            >
              <Trans i18nKey={"delivery.additionalServices.promoCodeApply"}>
                Apply
              </Trans>
            </LoadingButton>
          </div>
        )}
      </Grid>
    </Box>
  );
};

export default TotalCost;
